/**
 *
 * App.js
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 *
 */
import React, { useEffect } from 'react';
import { compose } from 'redux';
import { withRouter } from 'react-router';
import { Switch, Route } from 'react-router-dom';
import { ConnectedRouter } from 'connected-react-router/immutable';
import history from 'utils/history';
import CapSkeleton from '@capillarytech/cap-ui-library/CapSkeleton';
import { userIsAuthenticated, userIsNotAuthenticated } from '../../../utils/authWrapper';
import { sagaInjectorTypes } from '@capillarytech/vulcan-react-sdk/utils';

import Cap from '../Cap';
import NewLogin from '../NewLogin';
import withSuspence from '../../../utils/withSuspence';
import injectSaga from '../../../utils/injectSaga';
import injectReducer from '../../../utils/injectReducer';

import newLoginSaga from '../NewLogin/saga';
import newLoginReducer from '../NewLogin/reducer';
import { NEW_LOGIN_REDUCER } from '../NewLogin/constants';

import GlobalStyle from '../../../global-styles';

const Login = React.lazy(() =>
  import(/* webpackChunkName: "hostpage-login" */ '../Login'),
);
import { getConfigValue } from 'utils/commonUtils';

const ForgotPassword = React.lazy(() =>
  import(/* webpackChunkName: "hostpage-forgot-password" */ '../ForgotPassword'),
);
const PasswordSet = React.lazy(() =>
  import(/* webpackChunkName: "hostpage-password-set" */ '../PasswordSet'),
);
const OTPPage = React.lazy(() =>
  import(/* webpackChunkName: "hostpage-otp" */ '../OTPPage'),
);
const AccountLocked = React.lazy(() =>
  import(/* webpackChunkName: "hostpage-account-locked" */ '../AccountLocked'),
);

const loginUrl = getConfigValue('login_url');
const newLoginUrl = getConfigValue('new_login_url');
const otpUrl = getConfigValue('otp_url');
const accountLockedUrl = getConfigValue('account_locked_url');
const forgotPasswordUrl = getConfigValue('forgot_password_url');
const passwordSetUrl = getConfigValue('set_password_url');

const Protected = withRouter(userIsAuthenticated(Cap));

// Skeleton everywhere instead of spinners — matches the MFE outlet loader.
const chunkSkeleton = (
  <div style={{ padding: 24 }}>
    <CapSkeleton active title paragraph={{ rows: 6 }} />
  </div>
);

const LoginWithSuspence = withSuspence(withRouter(userIsNotAuthenticated(Login)), chunkSkeleton);
const NewLoginWrapped = withRouter(userIsNotAuthenticated(NewLogin));
const ForgotPasswordWithSuspence = withSuspence(withRouter(userIsNotAuthenticated(ForgotPassword)), chunkSkeleton);
const PasswordSetWithSuspence = withSuspence(withRouter(userIsNotAuthenticated(PasswordSet)), chunkSkeleton);
const OTPPageWithSuspence = withSuspence(withRouter(userIsNotAuthenticated(OTPPage)), chunkSkeleton);
const AccountLockedWithSuspence = withSuspence(withRouter(userIsNotAuthenticated(AccountLocked)), chunkSkeleton);

export const App = () => (
  <>
    <ConnectedRouter history={history}>
      <Switch>
        <Route exact path={loginUrl}>
          <LoginWithSuspence />
        </Route>
        <Route exact path={newLoginUrl}>
          <NewLoginWrapped />
        </Route>
        <Route exact path={otpUrl}>
          <OTPPageWithSuspence />
        </Route>
        <Route exact path={accountLockedUrl}>
          <AccountLockedWithSuspence />
        </Route>
        <Route exact path={forgotPasswordUrl}>
          <ForgotPasswordWithSuspence />
        </Route>
        <Route exact path={passwordSetUrl}>
          <PasswordSetWithSuspence />
        </Route>
        <Route>
          <Protected />
        </Route>
      </Switch>
    </ConnectedRouter>
    <GlobalStyle />
  </>
  );


const withNewLoginSaga = injectSaga({
  key: NEW_LOGIN_REDUCER,
  saga: newLoginSaga,
  mode: sagaInjectorTypes.DAEMON,
});
const withNewLoginReducer = injectReducer({
  key: NEW_LOGIN_REDUCER,
  reducer: newLoginReducer,
});

export default compose(withNewLoginReducer, withNewLoginSaga)(App);
